<template lang="pug">
.app-calendar 
	FullCalendar(:options="calendarOptions")
	AppPopup(v-show="activeHoverResult", :activeResult="activeHoverResult", @closePopup="activeHoverResult = null")
</template>
	
<script>
import { format, sub, add } from "date-fns";
import { 
	computePosition, 
	flip, 
	shift, 
	offset,
	arrow
} from "@floating-ui/dom";

import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";

import AppPopup from './Popup';
import sameOriginCheck from "../../../global/mixins/sameOriginCheck";

import { mapMutations, mapActions, mapGetters } from 'vuex';

export default {
	name: "AppCalendar",
	props: {
		results: Array
	},
	components: {
		FullCalendar,
		AppPopup,
	},
	data() {
		return {
			calendarOptions: {
				height: "auto",
				plugins: [dayGridPlugin],
				initialView: "dayGridMonth",
				events: null,
				headerToolbar: {
					left: 'prev',
					center: 'title',
					right: 'next'
				},
				eventTimeFormat: {
					hour: "numeric",
					minute: "numeric",
					meridiem: "short"
				},
				eventMouseEnter: this.startHoverTimer,
				eventMouseLeave: this.clearHoverTimer,
				eventClick: this.handleClick,
				// This below handles the month change event
				datesSet: event => {
					// As the calendar starts from prev month and end in next month I take the day between the range
					var midDate = new Date((event.start.getTime() + event.end.getTime()) / 2);
					this.currentMonthDate = midDate;
					this.handleMonthChange(midDate);
				}
			},
			activeHoverResult: null,
			popupEl: null,
			hoverTimer: null,
			currentMonthDate: null,
		};
	},
	computed: {},
	methods: {
		...mapMutations(["setFilterValueWithId", "setStateValue", "firstRequestSent"]),
		...mapActions(["executeSearch"]),
		eventHover(info) {
			const hoveredEvent = info.el;
			this.activeHoverResult = this.results.find(result => result.id == info.event.id);
			const arrowElement = this.popupEl.querySelector(".app-popup__pointer");
			computePosition(hoveredEvent, this.popupEl, {
				placement: "top-start",
				middleware: [
					flip(), 
					shift(), 
					offset(4), 
					arrow({element: arrowElement})
				]
			}).then(({x,y, placement, middlewareData}) => {
				Object.assign(this.popupEl.style, {top: `${y}px`, left: `${x}px`});
				
				// Accessing the data
				const {x: arrowX, y: arrowY} = middlewareData.arrow;
			
				const staticSide = {
					top: 'bottom',
					right: 'left',
					bottom: 'top',
					left: 'right',
				}[placement.split('-')[0]];

				let adjustedX = arrowX;
				if (arrowX > 450) adjustedX = 450;
				Object.assign(arrowElement.style, {
					left: adjustedX != null ? `${adjustedX}px` : '',
					top: arrowY != null ? `${arrowY}px` : '',
					right: '',
					bottom: '',
					[staticSide]: '-8px',
				});
			});
			
		},
		startHoverTimer(info) {
			const hoveredEvent = info.el;
			this.hoverTimer = setTimeout(() => {
				if (hoveredEvent.matches("*:hover") || this.popupEl.matches("*:hover")) {
					this.eventHover(info);
				} else this.clearHoverTimer();
			}, 350);
		},
		clearHoverTimer() {
			if (this.hoverTimer) {
				clearTimeout(this.hoverTimer);
				this.hoverTimer = null;
			}
		},
		handleClick(info) {
			info.jsEvent.preventDefault();
			const url = info.el.getAttribute("href");
			const external = !sameOriginCheck(url, window.location);
			if (external || this.config.openInNewWindow) {
				window.open(url, "_blank");
			} else {
				window.open(url, "_self");
			}
		},
		handleMonthChange(midDate) {
			// Since setDates occurs when page is loaded, we skip the first instance since that is already handled in App.vue
			if (!this.firstRequestSent) return;
			this.activeHoverResult = null;
			let current = format(midDate, "LLLL_yyyy").toLowerCase();
			let previous = format(sub(midDate, {months: 1}), "LLLL_yyyy").toLowerCase();
			let next = format(add(midDate, {months: 1}), "LLLL_yyyy").toLowerCase();

			// This is to prevent the calendar from showing events from formerly selected months that arne't relevant to this new search
			let oneTooLate = format(add(midDate, {months: 2}), "LLLL_yyyy").toLowerCase();
			let oneTooEarly = format(sub(midDate, {months: 2}), "LLLL_yyyy").toLowerCase();

			this.setFilterValueWithId({id: current, value: true});
			this.setFilterValueWithId({id: previous, value: true});
			this.setFilterValueWithId({id: next, value: true});
			this.setFilterValueWithId({id: oneTooLate, value: false});
			this.setFilterValueWithId({id: oneTooEarly, value: false});
			this.executeSearch();
		},
		unfilterMonths(date) {
			let current = format(date, "LLLL_yyyy").toLowerCase();
			let previous = format(sub(date, {months: 1}), "LLLL_yyyy").toLowerCase();
			let next = format(add(date, {months: 1}), "LLLL_yyyy").toLowerCase();

			// This is to prevent the calendar from showing events from formerly selected months that arne't relevant to this new search
			let oneTooLate = format(add(date, {months: 2}), "LLLL_yyyy").toLowerCase();
			let oneTooEarly = format(sub(date, {months: 2}), "LLLL_yyyy").toLowerCase();

			this.setFilterValueWithId({id: current, value: false});
			this.setFilterValueWithId({id: previous, value: false});
			this.setFilterValueWithId({id: next, value: false});
			this.setFilterValueWithId({id: oneTooLate, value: false});
			this.setFilterValueWithId({id: oneTooEarly, value: false});
			this.executeSearch();

		},
		updateCalendarEvents(){
			this.calendarOptions.events = this.results.map(result => {
			let start = new Date(result.itemDate);
			let end = new Date(result.meta.endDate);
			if (!end || start.getTime() > end.getTime()) end = start;
			return {
				title: result.title,
				start,
				end,
				id: result.id,
				url: result.url
			}
		});
		}
	},
	created() {
		this.updateCalendarEvents();
	},
	mounted() {
		this.popupEl = document.querySelector(".app-popup");
	},
	beforeDestroy() {
		this.unfilterMonths(this.currentMonthDate);
	},
	watch:{
		results(newResults, oldResults) {
			this.updateCalendarEvents();
		},
	}
};
</script>

<style lang="scss" scoped>
</style>

<style lang="scss">
#bluesearch .app-calendar {

	// FullCalendar CSS Vars
	--fc-small-font-size: .85em;
	--fc-page-bg-color: #{$white};
	--fc-neutral-bg-color: #{$white};
	--fc-neutral-text-color: grey;
	--fc-border-color: #{$lightGrayOrg};
	--fc-button-text-color: #fff;
	--fc-button-bg-color: #2c3e50;
	--fc-button-border-color: #2c3e50;
	--fc-button-hover-bg-color: #1e2b37;
	--fc-button-hover-border-color: #1a252f;
	--fc-button-active-bg-color: #1a252f;
	--fc-button-active-border-color: #151e27;
	--fc-event-bg-color: #3788d8;
	--fc-event-border-color: #3788d8;
	--fc-event-text-color: #fff;
	--fc-event-selected-overlay-color: rgba(0,0,0,.25);
	--fc-more-link-bg-color: #d0d0d0;
	--fc-more-link-text-color: inherit;
	--fc-event-resizer-thickness: 8px;
	--fc-event-resizer-dot-total-width: 8px;
	--fc-event-resizer-dot-border-width: 1px;
	--fc-non-business-color: hsla(0,0%,84%,.3);
	--fc-bg-event-color: #8fdf82;
	--fc-bg-event-opacity: 0.3;
	--fc-highlight-color: rgba(188,232,241,.3);
	--fc-today-bg-color: rgba(255,220,40,.15);
	--fc-now-indicator-color: red;

	table {
		margin-bottom: 0;
		thead th, 
		thead td,
		tbody th,
		tbody td {
			background: $white;
			&:not(:last-child) {
				border-right: unset;
			}
		}
		thead {
			th {
				a {
					color: $black;
					text-transform: uppercase;
					&:hover {
						color: $black;
					}
				}
			}

			.fc {
				&-col-header-cell-cushion {
					padding: 0.5rem;
				}
			}
		}
		tbody {
			tr {
				&:nth-child(2n) {
					background: $white;
				}
			}

			.fc {
				&-day-today {
					background: transparent;
					.fc-daygrid-day-number {
						background: $lightBlueOrg;
						font-weight: 600;
					}
				}
				&-daygrid {
					&-day {
						&-frame {
							padding-top: 0.5rem;
						}
						&-top {
							flex-direction: row;
							justify-content: center;
							a {
								color: $black;
								&:hover {
									color: $black;
								}
							}
						}
						&-number {
							font-size: 0.875rem;
							padding: 0;
							width: 1.75rem;
							min-width: 1.75rem;
							height: 1.75rem;
							border-radius: 50%;
							display: flex;
							justify-content: center;
							align-items: center;
						}
						&-events {
							min-height: 4rem;
							margin-bottom: 0.5rem;
							padding: 0 0.25rem;
						}
					}

					&-event.fc-daygrid-dot-event,
					.fc-daygrid-event-harness,
					.fc-h-event {
						margin-bottom: 0.5rem;
					}
					&-event,
					&-event.fc-h-event {
						display: block;
						white-space: break-spaces;
						border: none;
						.fc-event-main {
							color: $black;
							&-frame {
								display: block;
							}
						}
						&:is(a) {
							color: $black;
						}
						.fc-daygrid-event-dot {
							border: none;
							display: none;
						}
						.fc-event-time,
						.fc-event-title {
							overflow: unset;
						}
						.fc-event-time {
							font-weight: 700;
							display: inline-block;
							&::after {
								content: ":";
							}
						}
						.fc-event-title {
							display: inline;
							font-weight: 400;
							&-container {
								display: contents;
							}
						}
					}
					&-event {
						padding: 0.625rem;
						background: $lightBlueAltOrg;
						border-radius: 0.625rem;
					}
					&-event.fc-h-event {
						border-bottom: solid 0.5rem transparent;
						background: transparent;
						padding: 0;
						.fc-event-main {
							padding: 0.625rem;
							background: $lightBlueAltOrg;
							border-radius: 0.625rem;
						}

						&.fc-event-end {
							.fc-event-main {
								border-top-left-radius: 0;
								border-bottom-left-radius: 0;
							}
						}
						&.fc-event-start {
							.fc-event-main {
								border-top-right-radius: 0;
								border-bottom-right-radius: 0;
							}
						}
					}
					&-event-harness-abs {
						.fc-h-event:not(.fc-event-start, .fc-event-end) {
							.fc-event-main {
								border-radius: 0;
							}
						}
					}
				}
			}
		} 
	}
	.fc {
		&-header-toolbar {
			&.fc-toolbar {
				background: $blueOrg;
				padding: 1.25rem;
				margin-bottom: 0;
				justify-content: center;
				gap: 12%;
				border-radius: 0.75rem 0.75rem 0 0;
			}
			.fc-toolbar {
				&-title {
					color: $white;
					font-size: 1.625rem;
					line-height: 2.125rem;
				}
				&-chunk {
					button.fc-button {
						padding: 0;
						display: flex;
						justify-content: center;
						align-items: center;
						background: $yellowOrg;
						height: 2.5rem;
						max-height: 2.5rem;
						width: 2.5rem;
						min-width: 2.5rem;
						border-radius: 50%;
						span.fc-icon {
							color: $blueOrg;
						}

						&:hover {
							background: $lightYellowOrg;
						}
					}
				}
			}
		}
	}
}
</style>